import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { detectChangesIdle, idleMapSequential } from '@app/_v3/helpers/oc-idle';
import { ComponentCacheService } from '@app/_v3/services/component-cache.service';
import type { EDataLoaderType } from '@app/_v3/services/srvdefaults.config';
import { AVAILABLE_BFF_V2_ROUTES, BffV2Service } from '@app/core/services/bff/bff-v2.service';
import { ChangableComponent } from '@app/models/changable.component';
import { DEFAULT_PAGE_SIZE } from '@app/models/constants';
import { CLibraryFilterData } from '@app/store/models';
import { getMe } from '@app/store/reducers/profile.reducer';
import {
  FeaturesService,
  UtilsService,
  BffService,
  BFF_ROUTES,
  BffCacheManager,
  ContentService,
} from '@core/services/';
import { Store } from '@ngrx/store';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import type { BffGetContentEndpoint } from 'lingo2-api-models';
import {
  CurrencyEnum,
  EContentPaneFormat,
  EContentPanelRoute,
  FindContentStrategyEnum,
  IContentPane,
  IFindCategoriesFilter,
  TenantEnum,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, of } from 'rxjs';
import { filter, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { PaneComponent } from '../pane/pane.component';

/*
 */
const directoriesFilter: Partial<IFindCategoriesFilter> = {
  baseRoute: EContentPanelRoute['my-library/lessons'],
  use: FindContentStrategyEnum.library,
  language_id: [44, 4, 2, 1],
  subject_id: [],
  locale: 'en',
};
const DEF_PANE_CATEGORIES = {
  always_think_as_ampty: true,
  order: 1,
  format: EContentPaneFormat['categories'],
  displayMode: 'simple',
  loaded: true,
  directoriesFilter,
};

/*
 */
@Component({
  selector: 'app-panes',
  templateUrl: './panes.component.html',
  styleUrls: ['./panes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanesComponent extends ChangableComponent implements OnInit, OnDestroy, AfterViewInit {
  private _TAG = `[***] ${this.constructor.name}`;
  @ViewChild('panesHost', { read: ViewContainerRef, static: false }) panesHost: ViewContainerRef;

  /*
   */
  private features: FeaturesService = inject(FeaturesService);
  private readonly store: Store = inject(Store);
  public utilsService: UtilsService = inject(UtilsService);
  public contentService: ContentService = inject(ContentService);
  public deviceService: DeviceDetectorService = inject(DeviceDetectorService);
  private injector: EnvironmentInjector = inject(EnvironmentInjector);
  protected readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly bff1_srv: BffService = inject(BffService); // TODO. Refactor and remove or merge
  private readonly bff2_srv: BffV2Service = inject(BffV2Service); // TODO. Refactor and remove or merge
  private readonly cache: BffCacheManager = inject(BffCacheManager<IContentPane[]>);
  private readonly comp_cache: ComponentCacheService = inject(ComponentCacheService);

  /*
   */
  private _me: User;
  private me$ = new BehaviorSubject<User>(null);
  public showNotFound = false;
  public isMobile = false;
  public loading = true;

  private _panes: IContentPane[] = [];

  public contentFilter: CLibraryFilterData;
  public paneContentCounter = 0;
  public paneHasContentCounter = 0;
  public paneCategories: IContentPane = DEF_PANE_CATEGORIES;
  private currentLoader: any = null;
  private prev_url_base: string = null;
  private prev_url_full: string = '';

  /*
   */
  public constructor() {
    super();

    this.isMobile = this.deviceService.isMobile();

    this.destroyed$.pipe(take(1), takeUntil(this.destroyed$)).subscribe(() => {
      this.forceStopDataLoad();
      this.clearPaneContent(true);
    });
  }

  /* NB!
   */
  private async forceStopDataLoad() {
    // this._panes = [];
    try {
      await this.currentLoader?.abort();
    } catch (err) {
      console.warn('Error during abort:', err);
    }
  }

  public ngOnDestroy(): void {
    this.clearPaneContent(true);
    super.ngOnDestroy();
  }

  /*
   */
  public ngOnInit() {
    this.init_me();
    this.init_lib();
  }

  /*
   */
  ngAfterViewInit(): void {}

  /*
   */
  // private panes$ = combineLatest([this.panes$, this.me$]).pipe(
  //   map(([all, me]) => {
  //     const hidden = new Set<EContentPaneFormat>();
  //     const isTeacher = AccountService.isAsIfTeacher(me);

  //     if (me?.status === UserStatusEnum.guest) hidden.add(EContentPaneFormat['my-classes-students']);
  //     if (this.isMeVersion()) {
  //       hidden.add(EContentPaneFormat['my-classes-homework']);
  //       hidden.add(isTeacher ? EContentPaneFormat['my-classes-tutors'] : EContentPaneFormat['my-classes-students']);
  //     }
  //     return all.filter((p) => !hidden.has(p.format));
  //   })
  // );

  /*
   */
  public panesCount = () => this._panes?.length;
  private isPaneFixed = (val: number) =>
    this._panes?.[val] && this._panes[val].format === EContentPaneFormat.categories;

  /*
   */
  private init_me() {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((me) => {
        this._me = me;
        this.me$.next(me);
      });
  }

  /*
   */
  private isSameRoute(data: any): boolean {
    const serialized = JSON.stringify(data);
    const baseroute = data.baseRoute?.split('/')?.[0];
    const changed = baseroute !== this.prev_url_base; // || serialized !== prev_full;

    this.prev_url_base = baseroute;
    this.prev_url_full = serialized;

    this.clearPaneContent(changed);
    return true;
  }

  /*
   */
  private isUsefulRoute(data: any): boolean {
    if (!data?.languageCodes?.length || data?.hasProfile !== true || data?.url === '%2Fclear') return false;
    this.contentFilter = data;
    console.debug(`${this._TAG}: ContentFilter: `, this.contentFilter);

    return this.isSameRoute(data);
  }

  /*
   */
  private init_lib() {
    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((d) => this.isUsefulRoute(d)),
        tap(() => void this.loadPanes()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /*
   */
  private clearPaneContent(force?: boolean) {
    if (force === true || !this.isPaneFixed(0)) {
      // this.comp_cache?.detach(this.panesHost);
      this._panes = [];
      this.panesHost?.clear();
      this.resetCounters();
    } else {
      // Fixed categories/etc pane
      this._panes = this._panes.slice(0, 1);
      this.resetCounters(1);
    }
    this.showNotFound = false;
    this.comp_cache?.detach(this.panesHost, 0);
  }

  /*
   */
  private resetCounters(val: number = 0): void {
    this.paneContentCounter = this.paneHasContentCounter = val;
  }

  /*
   */
  private async renderPanesArr(panes: IContentPane[]) {
    await idleMapSequential(panes, (pane, i) => {
      if (this.isPaneFixed(0) && i === 0 && this.panesHost.length > 0) return;
      return this.renderPane(pane, i);
    });
  }

  /*
   */
  private renderPane(pane: IContentPane, index?: number): Promise<void> {
    if (pane.payload__content) {
      // Todo. Tmp for back comp.
      pane.contentItems = Array.isArray(pane.payload__content) ? pane.payload__content : [];
    }

    const key = `panes_${pane.format}_${pane.id ?? pane.order}`;
    const ref = this.comp_cache.initComponent(key, PaneComponent, this.panesHost, this.injector, { item: pane });
    if (!ref || !ref.hostView) return;

    ref.instance.loadFinished.pipe(take(1), takeUntil(this.destroyed$)).subscribe((has) => {
      if (index > 0) ref?.location?.nativeElement?.classList.add('__c__fadein');
      this.onPaneLoaded(pane, has);
    });
    detectChangesIdle(ref.changeDetectorRef);
  }

  /*
   */
  private getFilterKey(f: CLibraryFilterData): string {
    const { baseRoute, term, currency, subjectsMap } = f;
    return JSON.stringify({ baseRoute, term, currency, subjectsMap, filters: (f as any)?.filters });
  }

  private get_data_loader(): (key: string) => void {
    const route = this.contentFilter.baseRoute;
    const dataLoader = {
      bff2: this.dataLoaderFromBff2.bind(this),
      bff1: this.dataLoaderFromBff1.bind(this),
      store: this.dataLoaderDefault.bind(this),
    };

    if (BFF_ROUTES.includes(route)) {
      if (AVAILABLE_BFF_V2_ROUTES.includes(route)) {
        this.currentLoader = this.bff2_srv;
        return dataLoader.bff2;
      }
      this.currentLoader = this.bff1_srv;
      return dataLoader.bff1;
    } else {
      this.currentLoader = null;
      return dataLoader.store;
    }
  }

  /*
   */
  private async loadPanes(): Promise<void> {
    try {
      if (!this.utilsService.isContentPage) {
        // Immediately mark as loading to hide headers dependent on previous state
        this.loading = true;
        this.markForCheck();
      }

      await this.forceStopDataLoad();
      this.clearPaneContent();
      setTimeout(() => {
        this.get_data_loader()(this.getFilterKey(this.contentFilter));
      }, 0); // NB! Don't change, here we r doing 'microtick' to wait signal.abort execution.
    } catch (err) {
      console.error(`${this._TAG} loadPanes error:`, err);
      this.showNotFound = true;
    }
  }

  /*
   */
  private dataLoaderDefault(key: string): void {
    this.contentService
      .getFeaturings(this.contentFilter)
      .pipe(
        map((res: any) => res?.data as IContentPane[]),
        tap((res: any) => void this.onDataLoaded()(res)),
        finalize(this._finalize),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  /*
   */
  private dataLoaderFromBff2(): Promise<void> {
    this.setDefCurrency();
    const cf = this.contentFilter;
    return this.streamAndRender(this.bff2_srv.getContentStream(cf as BffGetContentEndpoint.Body), 'bff2');
  }

  /*
   */
  private dataLoaderFromBff1(): Promise<void> {
    const cf = this.contentFilter;
    return this.streamAndRender(this.bff1_srv.getContentStream(cf), 'bff1');
  }

  /*
   */
  private async streamAndRender(iter: AsyncIterable<any>, label: 'bff1' | 'bff2'): Promise<void> {
    try {
      let i = -1;
      for await (const x of iter) {
        const item = x?.item ?? x;
        if (!item) return;
        if (++i === 0 && this.isPaneFixed(0)) continue;
        this._panes.push(item);
        await this.renderPane(item, i);
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') console.debug(`${this._TAG} ${label}: NDJ stream aborted.`);
      else console.error(`${this._TAG} ${label} stream error`, e);
    } finally {
      this._finalize();
    }
  }

  /*
   */
  private _finalize = () => {
    this.loading = false;
  };

  /*
   */
  private onDataLoaded = () => async (res: IContentPane[]) => {
    if (!res || !Array.isArray(res)) {
      console.warn(`${this._TAG} onDataLoaded!: skipped because res is blank! `, res, this._panes);
      return;
    }

    this._panes = Array.isArray(res) ? res : [];
    this._finalize();
    await this.renderPanesArr(this._panes);
  };

  /* Todo. Return panel content from paneComponent
   */
  public onPaneLoaded(pane: IContentPane, hasContent: boolean): void {
    if (pane.format === EContentPaneFormat.categories) return;

    const isMainSearch = pane?.baseRoute === 'main' && !!pane?.contentFilter?.term;
    const noPayloadContent = !pane?.contentItems?.length;
    this.paneContentCounter += 1;
    this.paneHasContentCounter += hasContent ? 1 : 0;
    this.paneHasContentCounter -= isMainSearch && noPayloadContent && hasContent ? 1 : 0;
    const allLoaded = this.paneContentCounter >= this.panesCount();
    const hasAnyContent = this.paneHasContentCounter > 0;
    this.showNotFound = allLoaded ? !hasAnyContent : this.showNotFound;
    this.markForCheck();
  }

  /*
   */
  private setDefCurrency(): void {
    if (!this.contentFilter.currency) {
      const id = this._me?.currency_id ?? CurrencyEnum.usd;
      this.contentFilter = {
        ...this.contentFilter,
        currency: Object.keys(CurrencyEnum)[Object.values(CurrencyEnum).indexOf(id)]?.toUpperCase(),
      };
    }
  }

  public get isNewsHeader(): boolean {
    return ['news', 'my-classes', 'my-library', 'favorites'].includes(this.contentFilter?.baseRoute.split('/')[0]);
  }

  public isMeVersion() {
    return this.features.tenant === TenantEnum.onclass_me;
  }

  public trackByFn(index: any, pane: IContentPane) {
    return pane.order;
  }
}
